/**
 * THE DOOR to every page of the new design (PV1b: generalised from the home page's door, design-system §7b).
 *
 * It is on the first download, so it holds nothing but the one dynamic import of the SHELL (the `pavilion` chunk —
 * the frame, header, footer, stylesheet, drawn water and the page registry); the shell's registry then loads the page's
 * own chunk (`pavilion-<key>`), through the kit's chunk (`pavilion-kit`) for every page but the home page. The registry and the shell are imported for their TYPES only: a value import
 * would carry every page's loader onto every visitor's first download (scripts/check-chat-first-paint.mjs R4).
 *
 * WHY useDeferredModule AND NOT React.lazy: App.tsx records React.lazy stalling on the FIRST mount of `/` in
 * production (2026-05-09), and a door mounts on exactly that first render. The hook is a plain import() resolved into
 * state — no Suspense, nothing thrown during render — and it retries a failed download three times before handing a
 * chunk that is gone for good to the stale-tab self-heal (src/utils/useDeferredModule.ts).
 *
 * THE DOOR WAITS FOR BOTH CHUNKS, THEN RENDERS ONCE. Its loader is one promise — the shell, then the page — so the
 * arrival is still one state update. While they arrive the door paints the pavilion's sky in the theme the page will
 * open in, so the arrival is a calm fade rather than a flash of the wrong ground. If either cannot be had at all, the
 * visitor gets today's page — the same app — never a blank one.
 *
 * ONE LIGHT FROM PAGE TO PAGE (D-92, Miles 2026-10-24: "when transitioning between dark mode page to dark mode page …
 * it goes light mode blank before loading"):
 *  - a page already downloaded this visit renders in the move's own commit, never behind a frame of the door (the
 *    `loaded` map below; only a download that succeeded is kept, so a failure is tried again next time);
 *  - an app page's door hands its light to the app header above it while the chunks arrive (./waitingLight.ts, the
 *    light a route's guard in front of the page waits in too), as the page's frame does once it lands, so the band
 *    keeps the light through the move;
 *  - a wait past DOOR_SLOW_MS shows the working mark on the sky, in the sky's inks: a slow first visit says it is
 *    working rather than standing blank;
 *  - the app header's door holds the band's place with no ground of its own: the document's ground under it is the
 *    page's light (src/styles/theme.ts).
 */
import React from 'react';
import { useDeferredModule } from '../../utils/useDeferredModule';
import { semanticDark, semanticLight } from '../../styles/semanticTokens.generated';
import { designShows } from '../previewPath';
import { PageLight } from '../PageLight';
import { markPavilionPage, type PavilionPageMark } from '../pageAhead';
import { Spinner } from '../../components/common/Spinner';
import { preloadPavilionFont } from './pavilionFont';
import { claimAppChrome } from './appChrome';
import { useWaitingLight } from './waitingLight';
import type { PavilionPageKey } from './shell/pageRegistry';
import type { PavilionShellProps } from './shell/PavilionShell';
import type { PavilionTopUpDialogProps } from './topup/PavilionTopUpDialog';
import type { PavilionTermsDialogProps } from './terms/PavilionTermsDialog';
import type * as FilePreviewNamespace from './filepreview/PavilionFilePreview';
import type { PavilionFilePreviewOverlayProps, PavilionFloatingPreviewWindowProps } from './filepreview/PavilionFilePreview';
import type { PavilionBannerBand, PavilionInstallGuide } from './band/PavilionBannerBand';
import type { TeamRolesInviteProps } from './pages/team-roles/TeamRolesInvite';
import type { BannerBandView } from '../../components/common/GlobalBannerCenter';

export type { PavilionPageKey };

// The ONE import of the shell (R4 of scripts/check-chat-first-paint.mjs counts it): every door and the boot-time
// preload call it, and webpack shares one request between them.
const loadShell = () => import(/* webpackChunkName: "pavilion" */ './shell/PavilionShell');

export interface LoadedPavilion {
  Shell: React.ComponentType<PavilionShellProps>;
  Page: React.ComponentType;
}

/** Every page this visit has downloaded, kept once its chunks have arrived (never on a failure). */
const loaded = new Map<PavilionPageKey, LoadedPavilion>();

/** The shell, then the page it frames, as one promise. */
export function loadPavilion(key: PavilionPageKey): Promise<LoadedPavilion> {
  return loadShell().then((shell) => shell.loadPage(key).then((Page) => {
    const pavilion = { Shell: shell.default, Page };
    loaded.set(key, pavilion);
    return pavilion;
  }));
}

/**
 * Start fetching a page before the first render (src/index.tsx calls it for `/` when the page will show the new
 * design), so both requests are on the wire while main.js is still being parsed instead of after the router mounts
 * the door. Never throws; a failure is the door's to handle (it retries, then falls back to today's page).
 */
export function preloadPavilion(key: PavilionPageKey): void {
  preloadPavilionFont();
  loadPavilion(key).catch(() => undefined);
}

export interface PavilionDoorProps {
  pageKey: PavilionPageKey;
  /** What renders if the new design's chunks cannot be loaded: today's page. */
  fallback: React.ReactNode;
  /**
   * The page stands under the app header (its registry row says chrome 'app'), which App.tsx draws: the door claims no
   * header for it. Declared where App.tsx declares the page, because the door cannot read the registry's values.
   */
  appChrome?: boolean;
}

/** How long the door's sky stands empty before it shows the working mark (D-92). */
export const DOOR_SLOW_MS = 400;

/** True once a wait has lasted DOOR_SLOW_MS; false again when it ends. */
function useSlowWait(waiting: boolean): boolean {
  const [slow, setSlow] = React.useState(false);
  React.useEffect(() => {
    setSlow(false);
    if (!waiting) return undefined;
    const timer = window.setTimeout(() => setSlow(true), DOOR_SLOW_MS);
    return () => window.clearTimeout(timer);
  }, [waiting]);
  return waiting && slow;
}

export const PavilionDoor: React.FC<PavilionDoorProps> = ({ pageKey, fallback, appChrome = false }) => {
  // The face travels while the chunks do, so the headline paints in it rather than swapping to it.
  React.useEffect(() => preloadPavilionFont(), []);
  const { module: arrived, error } = useDeferredModule(() => loadPavilion(pageKey), `pavilion:${pageKey}`, { start: 'mount' });
  // A page already downloaded this visit is drawn at once (D-92): the move to it shows no frame of the door.
  const module = arrived ?? loaded.get(pageKey) ?? null;
  // The page brings its own header: today's global one stands down while the door is loading or showing it, and
  // comes back if the door falls back to today's page (./appChrome.ts; App.tsx reads the claim). An app page brings
  // none: the app header above it is App.tsx's, so it claims nothing.
  React.useLayoutEffect(() => (error || appChrome ? undefined : claimAppChrome('header')), [error, appChrome]);

  // The light the sky paints in: the visitor's choice in this browser, else their device's. An app page's door hands
  // it, with the switch, to the app header above while the chunks arrive, as the page's frame does once it lands
  // (./waitingLight.ts): the band never falls back to noon between two pages.
  const waiting = !module && !error;
  const theme = useWaitingLight(appChrome, waiting);
  const slow = useSlowWait(waiting);

  if (module) {
    const { Shell, Page } = module;
    return <Shell pageKey={pageKey} Page={Page} />;
  }
  if (error) return <>{fallback}</>;

  const dark = theme === 'dark';
  return (
    <>
      {/* The chat takes the light the page opens in, even while the page's chunk arrives (PR-4a.2); never in the
          fallback above, which is today's light-only page. */}
      <PageLight theme={theme} />
      <div
        data-pavilion-door=""
        aria-busy="true"
        style={{ minHeight: '100vh', background: dark ? semanticDark.canvas : semanticLight.canvas, display: 'grid', placeItems: 'center' }}
      >
        {slow && <Spinner $onDark={dark} />}
      </div>
    </>
  );
};

/** The app header once it has arrived this visit (never on a failure), drawn at once wherever its door mounts again. */
let loadedAppHeader: React.ComponentType | null = null;

/** The app header in the new design (PV5-8:D), through the shell (the one import of it, above) and the kit. */
export function loadPavilionAppHeader(): Promise<React.ComponentType> {
  return loadShell().then((shell) => shell.loadAppHeader()).then((AppHeader) => {
    loadedAppHeader = AppHeader;
    return AppHeader;
  });
}

/** Tests only: forget every page and the app header this visit has downloaded. */
export function resetLoadedPavilionsForTests(): void {
  loaded.clear();
  loadedAppHeader = null;
}

/** The band's height while it arrives: the new header's own (72px, 64px at 720px and under), so nothing moves when it lands. */
function appHeaderHeight(): number {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 720px)').matches ? 64 : 72;
}

export interface PavilionAppHeaderDoorProps {
  /** What renders if the new design's chunks cannot be loaded: today's header. */
  fallback: React.ReactNode;
}

/**
 * THE DOOR to the app header in the new design (PV5-8:D): App.tsx draws it where today's header stands, while the
 * `app` surface shows. Loaded the pages' way (a plain import() resolved into state, never React.lazy), and today's
 * header is its fallback if the chunks cannot be had. While they arrive it holds the band's place at the band's height,
 * on no ground of its own: the document's ground under it is the light of the page below (D-92).
 */
export const PavilionAppHeaderDoor: React.FC<PavilionAppHeaderDoorProps> = ({ fallback }) => {
  React.useEffect(() => preloadPavilionFont(), []);
  const { module: arrived, error } = useDeferredModule(loadPavilionAppHeader, 'pavilion:app-header', { start: 'mount' });
  const AppHeader = arrived ?? loadedAppHeader;
  if (AppHeader) return <AppHeader />;
  if (error) return <>{fallback}</>;
  return <div data-pavilion-app-door="" aria-busy="true" style={{ height: appHeaderHeight() }} />;
};

/** The top-up dialog in the new design (PV10 part 2), through the shell (the one import of it, above) and the kit. */
export function loadPavilionTopUpDialog(): Promise<React.ComponentType<PavilionTopUpDialogProps>> {
  return loadShell().then((shell) => shell.loadTopUpDialog());
}

/** Fetch the top-up dialog's chunks ahead of a press. Never throws: a failure is the door's to handle. */
export function preloadPavilionTopUpDialog(): void {
  loadPavilionTopUpDialog().catch(() => undefined);
}

export interface PavilionTopUpDoorProps extends PavilionTopUpDialogProps {
  /** What renders if the new design's chunks cannot be loaded: today's dialog, so a person can always pay. */
  fallback: React.ReactNode;
}

/**
 * THE DOOR to the top-up dialog in the new design (PV10 part 2): TopUpModalProvider draws it, inside Stripe's provider,
 * where the `top-up` surface shows. Loaded the pages' way (a plain import() resolved into state, never React.lazy), with
 * today's dialog as its fallback. It draws nothing while the chunks arrive: the provider fetches them at the first idle
 * moment for a signed-in person (preloadPavilionTopUpDialog), so by the time anyone presses "top up" they are here.
 */
export const PavilionTopUpDoor: React.FC<PavilionTopUpDoorProps> = ({ fallback, ...props }) => {
  React.useEffect(() => preloadPavilionFont(), []);
  const { module: TopUpDialog, error } = useDeferredModule(loadPavilionTopUpDialog, 'pavilion:top-up', { start: 'mount' });
  if (TopUpDialog) return <TopUpDialog {...props} />;
  if (error) return <>{fallback}</>;
  return null;
};

/** The terms dialog in the new design (SOC 2 Phase 1's one-time dialog), through the shell (the one import of it, above) and the kit. */
export function loadPavilionTermsDialog(): Promise<React.ComponentType<PavilionTermsDialogProps>> {
  return loadShell().then((shell) => shell.loadTermsDialog());
}

/** Fetch the terms dialog's chunks before it is shown. Never throws: a failure is the door's to handle. */
export function preloadPavilionTermsDialog(): void {
  preloadPavilionFont();
  loadPavilionTermsDialog().catch(() => undefined);
}

export interface PavilionTermsDoorProps extends PavilionTermsDialogProps {
  /** What renders if the new design's chunks cannot be loaded: today's dialog, so a person can always answer it. */
  fallback: React.ReactNode;
}

/**
 * THE DOOR to the terms dialog in the new design (SOC 2 Phase 1's one-time dialog): TermsUpdateHost draws it where the
 * `terms-update` surface shows. Loaded the pages' way (a plain import() resolved into state, never React.lazy), with
 * today's dialog as its fallback. It draws nothing while the chunks arrive: the host fetches them at the first idle moment
 * once it may ask (preloadPavilionTermsDialog: a signed-in person with no record of the current documents), so a person
 * who has agreed, and a visitor who is signed out, never download them.
 */
export const PavilionTermsDoor: React.FC<PavilionTermsDoorProps> = ({ fallback, ...props }) => {
  React.useEffect(() => preloadPavilionFont(), []);
  const { module: TermsDialog, error } = useDeferredModule(loadPavilionTermsDialog, 'pavilion:terms', { start: 'mount' });
  if (TermsDialog) return <TermsDialog {...props} />;
  if (error) return <>{fallback}</>;
  return null;
};

/** The banner band in the new design (PV5-8:D part 2), through the shell (the one import of it, above) and the kit. */
export function loadPavilionBannerBand(): Promise<typeof PavilionBannerBand> {
  return loadShell().then((shell) => shell.loadBannerBand());
}

export interface PavilionBannerBandDoorProps {
  /** What the band shows now, from GlobalBannerCenter; null once the last notice has gone (the band slides away). */
  view: BannerBandView | null;
  /** What renders if the new design's chunks cannot be loaded: today's band, so a notice is never lost. */
  fallback: React.ReactNode;
}

/**
 * THE DOOR to the banner band in the new design (PV5-8:D part 2): GlobalBannerCenter draws it where today's strip stands,
 * while the `band` surface shows, from the first notice on (never before: a page with nothing to say fetches nothing).
 * Loaded the pages' way (a plain import() resolved into state, never React.lazy), with today's band as its fallback. It
 * draws nothing while the chunks arrive: on a signed-in page the app header has usually fetched the kit already.
 */
export const PavilionBannerBandDoor: React.FC<PavilionBannerBandDoorProps> = ({ view, fallback }) => {
  React.useEffect(() => preloadPavilionFont(), []);
  const { module: Band, error } = useDeferredModule(loadPavilionBannerBand, 'pavilion:band', { start: 'mount' });
  if (Band) return <Band view={view} />;
  if (error) return <>{fallback}</>;
  return null;
};

/** The phone install offer's guide in the new design (PV5-8:D part 2), from the banner band's chunk. */
export function loadPavilionInstallGuide(): Promise<typeof PavilionInstallGuide> {
  return loadShell().then((shell) => shell.loadInstallGuide());
}

export interface PavilionInstallGuideDoorProps {
  open: boolean;
  onClose: () => void;
  /** What renders if the new design's chunks cannot be loaded: today's sheet, so the steps are never lost. */
  fallback: React.ReactNode;
}

/**
 * THE DOOR to the phone install offer's guide in the new design (PV5-8:D part 2): InstallAppBanner.tsx draws it, from the
 * first time the guide opens, where the `band` surface shows. Its chunk is the band's, which drew the offer.
 */
export const PavilionInstallGuideDoor: React.FC<PavilionInstallGuideDoorProps> = ({ open, onClose, fallback }) => {
  const { module: Guide, error } = useDeferredModule(loadPavilionInstallGuide, 'pavilion:install-guide', { start: 'mount' });
  if (Guide) return <Guide open={open} onClose={onClose} />;
  if (error) return <>{fallback}</>;
  return null;
};

/** The invite the chat opens, in the new design (PV11, the chat's invite), through the shell (the one import of it, above) and the kit. */
export function loadPavilionTeamRolesInvite(): Promise<React.ComponentType<TeamRolesInviteProps>> {
  return loadShell().then((shell) => shell.loadTeamRolesInvite());
}

export interface PavilionTeamRolesInviteDoorProps extends TeamRolesInviteProps {
  /** What renders if the new design's chunks cannot be loaded: today's dialog, so an invitation can always be sent. */
  fallback: React.ReactNode;
}

/** The invite once it has been asked for: the new design's sheet once its chunks are here, today's if they cannot be had. */
const TeamRolesInviteAsked: React.FC<PavilionTeamRolesInviteDoorProps> = ({ fallback, ...props }) => {
  React.useEffect(() => preloadPavilionFont(), []);
  const { module: Invite, error } = useDeferredModule(loadPavilionTeamRolesInvite, 'pavilion:team-roles-invite', { start: 'mount' });
  if (Invite) return <Invite {...props} />;
  if (error) return <>{fallback}</>;
  return null;
};

/**
 * THE DOOR to the invite the chat opens, in the new design (PV11, the chat's invite): today's InviteModal hands over to it
 * where the `team-roles` surface shows. The chat draws its invite closed for every signed-in person, so nothing is fetched
 * until the invite first opens; from then on it is loaded the pages' way (a plain import() resolved into state, never
 * React.lazy), with today's dialog as its fallback. Team & Roles' chunk is rarely on hand by then, so the sheet comes a
 * moment after that first press.
 */
export const PavilionTeamRolesInviteDoor: React.FC<PavilionTeamRolesInviteDoorProps> = (props) => {
  const [asked, setAsked] = React.useState(props.isOpen);
  React.useEffect(() => {
    if (props.isOpen) setAsked(true);
  }, [props.isOpen]);
  return asked ? <TeamRolesInviteAsked {...props} /> : null;
};

/** The file preview in the new design (PV5-8:C part 2), through the shell (the one import of it, above) and the kit. */
export function loadPavilionFilePreview(): Promise<typeof FilePreviewNamespace> {
  return loadShell().then((shell) => shell.loadFilePreview());
}

/** The longest the idle fetch of the file preview may wait on a busy main thread. */
const PREVIEW_IDLE_TIMEOUT_MS = 4000;
let previewFetchAsked = false;

/**
 * Fetch the file preview's chunks at the browser's first idle moment, once per page: a file's tile calls it when it is
 * drawn where the `file-preview` surface shows (ChatAttachment.tsx), so the first press on a file opens the new viewer at
 * once, and a page with no file on it never fetches them. Never throws: a failure is the doors' to handle.
 */
export function preloadPavilionFilePreview(): void {
  if (previewFetchAsked || typeof window === 'undefined') return;
  previewFetchAsked = true;
  const fetchIt = () => { loadPavilionFilePreview().catch(() => undefined); };
  const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(fetchIt, { timeout: PREVIEW_IDLE_TIMEOUT_MS });
  else window.setTimeout(fetchIt, 1);
}

/** One preview door: the new design's preview once its chunks are here, today's if they cannot be had, nothing between. */
const FilePreviewDoor: React.FC<{ fallback: React.ReactNode; draw: (m: typeof FilePreviewNamespace) => React.ReactElement }> = ({ fallback, draw }) => {
  React.useEffect(() => preloadPavilionFont(), []);
  const { module, error } = useDeferredModule(loadPavilionFilePreview, 'pavilion:file-preview', { start: 'mount' });
  if (module) return draw(module);
  if (error) return <>{fallback}</>;
  return null;
};

export interface PavilionFilePreviewDoorProps extends PavilionFilePreviewOverlayProps {
  /** What renders if the new design's chunks cannot be loaded: today's preview, so a file always opens. */
  fallback: React.ReactNode;
}

/**
 * THE DOOR to the file viewer in the new design (PV5-8:C part 2): the chat's tiles (ChatAttachment.tsx) and the app-level
 * previewer (FloatingPreviewContext.tsx) draw it where the `file-preview` surface shows. Loaded the pages' way (a plain
 * import() resolved into state, never React.lazy), with today's overlay as its fallback.
 */
export const PavilionFilePreviewDoor: React.FC<PavilionFilePreviewDoorProps> = ({ fallback, ...props }) => (
  <FilePreviewDoor fallback={fallback} draw={(m) => <m.PavilionFilePreviewOverlay {...props} />} />
);

export interface PavilionFloatingPreviewDoorProps extends PavilionFloatingPreviewWindowProps {
  /** What renders if the new design's chunks cannot be loaded: today's window. */
  fallback: React.ReactNode;
}

/** THE DOOR to a floating window in the new design (PV5-8:C part 2): the app-level layer draws one per window. */
export const PavilionFloatingPreviewDoor: React.FC<PavilionFloatingPreviewDoorProps> = ({ fallback, ...props }) => (
  <FilePreviewDoor fallback={fallback} draw={(m) => <m.PavilionFloatingPreviewWindow {...props} />} />
);

/**
 * A page of the app, shown in the new design where its surface word says so. Wrap the page where App.tsx declares it
 * (`const SafeAbout = withSafeAnimation(withPavilion(About, 'about'))`), never on a route line: the route stays
 * `element={<SafeAbout />}`. The coverage gate follows the route to that one declaration. A page that stands under the
 * app header says so here (`withPavilion(AgentActivity, 'desktop', { chrome: 'app' })`), as its registry row does.
 *
 * The wrapper carries the page's mark (its key and chrome), so a route's guard in front of it can wait in the light
 * the page opens in without drawing the page (../pageAhead.ts; D-92).
 */
export function withPavilion<P extends object>(
  Today: React.ComponentType<P>,
  key: PavilionPageKey,
  options: { chrome?: 'app' } = {},
): React.FC<P> & { pavilionPage: PavilionPageMark } {
  const appChrome = options.chrome === 'app';
  const Wrapped: React.FC<P> = (props) =>
    designShows(key)
      ? <PavilionDoor pageKey={key} fallback={<Today {...props} />} appChrome={appChrome} />
      : <Today {...props} />;
  Wrapped.displayName = `withPavilion(${key})`;
  return markPavilionPage(Wrapped, { key, appChrome });
}
